export interface TourStep {
  target: string; // CSS selector
  title: string;
  description: string;
  placement: "top" | "bottom" | "left" | "right";
  navigateTo?: string; // route to navigate to
  switchPathway?: "learning" | "assessment"; // switch pathway before navigating
  pageKey?: string; // marks this page as toured when visited
}

// ─── Common steps (all roles) ───────────────────────────────────
const commonSteps: TourStep[] = [
  {
    target: "[data-tour='sidebar']",
    title: "Navigation Sidebar",
    description: "Use the sidebar to navigate between different sections of Lurneax. Click any item to explore.",
    placement: "right",
  },
  {
    target: "[data-tour='header']",
    title: "Quick Access Bar",
    description: "Access your profile, notifications, and account settings from the top bar.",
    placement: "bottom",
  },
];

// ─── Admin / Master Admin ───────────────────────────────────────
const adminSteps: TourStep[] = [
  {
    target: "[data-tour='nav-learning-dashboard']",
    title: "Learning Dashboard",
    description: "Your command centre — see course completions, team progress, and learning insights at a glance.",
    placement: "right",
  },
  {
    target: "[data-tour='nav-course-catalog']",
    title: "Course Catalog",
    description: "Create, edit, and manage all training courses for your network.",
    placement: "right",
  },
  {
    target: "[data-tour='nav-network-progress-center']",
    title: "Network Progress",
    description: "Monitor learning progress across all centres and staff members.",
    placement: "right",
  },
];

// ─── Center Manager — Learning sidebar with navigation ──────────
const centerManagerLearningSteps: TourStep[] = [
  {
    target: "[data-tour='nav-learning-dashboard']",
    title: "Learning Dashboard",
    description: "Overview of your centre's learning activity and staff progress.",
    placement: "right",
    navigateTo: "/learning/dashboard",
    pageKey: "learning-dashboard",
  },
  {
    target: "[data-tour='nav-my-progress']",
    title: "My Progress",
    description: "Track your own personal learning journey and course completions.",
    placement: "right",
    navigateTo: "/learning/my-progress",
    pageKey: "learning-my-progress",
  },
  {
    target: "[data-tour='nav-network-progress-center']",
    title: "Network Progress Center",
    description: "Drill into individual progress for staff at your centre.",
    placement: "right",
    navigateTo: "/learning/progress-center",
    pageKey: "learning-progress-center",
  },
  {
    target: "[data-tour='nav-course-catalog']",
    title: "Course Catalog",
    description: "Browse and assign courses to your centre's staff.",
    placement: "right",
    navigateTo: "/learning/courses",
    pageKey: "learning-courses",
  },
  {
    target: "[data-tour='nav-tutor-audit']",
    title: "Tutor Audit",
    description: "Review and audit tutor performance and compliance.",
    placement: "right",
    navigateTo: "/learning/tutor-audit",
    pageKey: "learning-tutor-audit",
  },
];

// ─── Understanding Lurneax step (CM & Tutor) ───────────────────
const understandingLurneaxStep: TourStep[] = [
  {
    target: "[data-tour='sidebar']",
    title: "Understanding Lurneax",
    description: "Before we finish, check out the full platform walkthrough — screenshots, features, and tips all in one place.",
    placement: "right",
    navigateTo: "/understanding-lurneax",
  },
];

// ─── Center Manager — switch to Assessment ──────────────────────
const centerManagerSwitchStep: TourStep[] = [
  {
    target: "[data-tour='sidebar']",
    title: "Switching to Assessments",
    description: "Now let's explore the Assessment side of Lurneax. We'll switch pathways for you.",
    placement: "right",
    switchPathway: "assessment",
    navigateTo: "/assessment/dashboard",
  },
];

// ─── Center Manager — Assessment sidebar with navigation ────────
const centerManagerAssessmentSteps: TourStep[] = [
  {
    target: "[data-tour='nav-diagnostic-tests']",
    title: "Diagnostic Tests",
    description: "Create and manage diagnostic assessments for your students.",
    placement: "right",
    navigateTo: "/assessment/diagnostic-tests",
    pageKey: "assessment-diagnostic-tests",
  },
  {
    target: "[data-tour='nav-end-of-term']",
    title: "End of Term",
    description: "Manage end-of-term tests and results for your centre.",
    placement: "right",
    navigateTo: "/assessment/end-of-term",
    pageKey: "assessment-end-of-term",
  },
  {
    target: "[data-tour='nav-exams-management']",
    title: "Exams Management",
    description: "Create formal exams with time limits and sections.",
    placement: "right",
    navigateTo: "/assessment/exams",
    pageKey: "assessment-exams",
  },
  {
    target: "[data-tour='nav-manual-scoring']",
    title: "Manual Scoring",
    description: "Score open-ended questions and review student submissions.",
    placement: "right",
    navigateTo: "/assessment/scoring",
    pageKey: "assessment-scoring",
  },
  {
    target: "[data-tour='nav-my-students']",
    title: "My Students",
    description: "View and manage all students enrolled at your centre.",
    placement: "right",
    navigateTo: "/assessment/students",
    pageKey: "assessment-students",
  },
  {
    target: "[data-tour='nav-early-warning-system']",
    title: "Early Warning System",
    description: "Identify at-risk students early with engagement and performance alerts.",
    placement: "right",
    navigateTo: "/assessment/early-warning",
    pageKey: "assessment-early-warning",
  },
  {
    target: "[data-tour='nav-parent-chat']",
    title: "Parent Chat",
    description: "Communicate directly with parents about student progress.",
    placement: "right",
    navigateTo: "/assessment/messages",
    pageKey: "assessment-messages",
  },
];

// ─── Tutor — Learning sidebar with navigation ───────────────────
const tutorLearningSteps: TourStep[] = [
  {
    target: "[data-tour='nav-learning-dashboard']",
    title: "Your Dashboard",
    description: "See your assigned courses, progress, and upcoming deadlines here.",
    placement: "right",
    navigateTo: "/learning/dashboard",
    pageKey: "learning-dashboard",
  },
  {
    target: "[data-tour='nav-my-progress']",
    title: "My Progress",
    description: "Track your personal learning journey and course completions.",
    placement: "right",
    navigateTo: "/learning/my-progress",
    pageKey: "learning-my-progress",
  },
  {
    target: "[data-tour='nav-course-catalog']",
    title: "Course Catalog",
    description: "Browse available courses and continue your professional development.",
    placement: "right",
    navigateTo: "/learning/courses",
    pageKey: "learning-courses",
  },
];

// ─── Tutor — switch to Assessment ───────────────────────────────
const tutorSwitchStep: TourStep[] = [
  {
    target: "[data-tour='sidebar']",
    title: "Switching to Assessments",
    description: "Now let's explore the Assessment pathway where you manage quizzes and students.",
    placement: "right",
    switchPathway: "assessment",
    navigateTo: "/assessment/dashboard",
  },
];

// ─── Tutor — Assessment sidebar with navigation ─────────────────
const tutorAssessmentSteps: TourStep[] = [
  {
    target: "[data-tour='nav-diagnostic-tests']",
    title: "Diagnostic Tests",
    description: "Create and manage diagnostic assessments for your students.",
    placement: "right",
    navigateTo: "/assessment/diagnostic-tests",
    pageKey: "assessment-diagnostic-tests",
  },
  {
    target: "[data-tour='nav-exam-results']",
    title: "Exam Results",
    description: "Review exam results and performance data for your students.",
    placement: "right",
    navigateTo: "/assessment/exam-results",
    pageKey: "assessment-exam-results",
  },
  {
    target: "[data-tour='nav-manual-scoring']",
    title: "Manual Scoring",
    description: "Score open-ended questions and review submissions.",
    placement: "right",
    navigateTo: "/assessment/scoring",
    pageKey: "assessment-scoring",
  },
  {
    target: "[data-tour='nav-my-students']",
    title: "My Students",
    description: "View all students assigned to you and track their progress.",
    placement: "right",
    navigateTo: "/assessment/students",
    pageKey: "assessment-students",
  },
  {
    target: "[data-tour='nav-early-warning-system']",
    title: "Early Warning System",
    description: "Get alerts about students who need extra support.",
    placement: "right",
    navigateTo: "/assessment/early-warning",
    pageKey: "assessment-early-warning",
  },
];

// ─── Parent steps (no navigation) ───────────────────────────────
const parentSteps: TourStep[] = [
  {
    target: "[data-tour='daily-nugget-widget']",
    title: "Today's Learning Nugget",
    description: "A short, bite-sized learning tip refreshed daily. Each nugget covers study strategies, parent guidance, or exam tips — check back every day for a new one!",
    placement: "bottom",
  },
  {
    target: "[data-tour='weekly-quiz-widget']",
    title: "Weekly Quiz Challenge",
    description: "A quick 3-question quiz aligned to your child's grade and curriculum. A new quiz appears every week to keep skills sharp!",
    placement: "bottom",
  },
  {
    target: "[data-tour='nav-diagnostic-test-results']",
    title: "Test Results",
    description: "Review your child's diagnostic test scores and progress trends.",
    placement: "right",
  },
  {
    target: "[data-tour='nav-assignments']",
    title: "Assignments",
    description: "View and track homework and assignments for your child.",
    placement: "right",
  },
];

// ─── Student steps (no navigation) ──────────────────────────────
const studentSteps: TourStep[] = [
  {
    target: "[data-tour='nav-quiz-dashboard']",
    title: "My Quizzes",
    description: "Access your assigned quizzes and practice assessments here.",
    placement: "right",
  },
];

export function getTourSteps(role: string | null): TourStep[] {
  const steps = [...commonSteps];

  switch (role) {
    case "master_admin":
    case "admin":
      steps.push(...adminSteps);
      break;
    case "center_manager":
      steps.push(
        ...centerManagerLearningSteps,
        ...centerManagerSwitchStep,
        ...centerManagerAssessmentSteps,
        ...understandingLurneaxStep
      );
      break;
    case "tutor":
      steps.push(
        ...tutorLearningSteps,
        ...tutorSwitchStep,
        ...tutorAssessmentSteps,
        ...understandingLurneaxStep
      );
      break;
    case "parent":
      steps.push(...parentSteps);
      break;
    case "student":
      steps.push(...studentSteps);
      break;
  }

  return steps;
}
